// Firma manuscrita digitalizada en tableta — puerto a hce-chart de
// packages/hce/src/hce/consentimiento/SignaturePad.tsx (Módulo H, Grupo 2).
// Mismo canvas nativo + Pointer Events, sin librería externa — solo cambia
// el estilo (Tailwind/hce-* en vez de Mantine).
import { Eraser, Pencil } from 'lucide-react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';

export type SignaturePadHandle = {
  isEmpty: () => boolean;
  toDataUrl: () => string;
  clear: () => void;
};

export interface SignaturePadProps {
  onChange?: (empty: boolean) => void;
}

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(function SignaturePad({ onChange }, ref) {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);

  useImperativeHandle(ref, () => ({
    isEmpty: () => empty,
    toDataUrl: () => canvasRef.current?.toDataURL('image/png') ?? '',
    clear: () => clearCanvas(),
  }));

  function setupCanvas(): void {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#20242b';
  }

  useEffect(() => {
    setupCanvas();
    const handle = (): void => setupCanvas();
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function pos(e: ReactPointerEvent<HTMLCanvasElement>): { x: number; y: number } {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function start(e: ReactPointerEvent<HTMLCanvasElement>): void {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    canvasRef.current?.setPointerCapture(e.pointerId);
  }

  function move(e: ReactPointerEvent<HTMLCanvasElement>): void {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    if (empty) {
      setEmpty(false);
      onChange?.(false);
    }
  }

  function end(): void {
    drawing.current = false;
  }

  function clearCanvas(): void {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    onChange?.(true);
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="relative overflow-hidden rounded-md border-2 border-dashed border-hce-line bg-white">
        <canvas
          ref={canvasRef}
          aria-label={t('consentimiento.areaFirma')}
          className="block h-44 w-full cursor-crosshair touch-none"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
        />
        {empty && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 text-hce-muted">
            <Pencil className="size-5" />
            <span className="text-sm">{t('consentimiento.firmeAqui')}</span>
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <Button type="button" variant="ghost" size="icon" onClick={clearCanvas} aria-label={t('consentimiento.limpiarFirma')}>
          <Eraser className="size-4" />
        </Button>
      </div>
    </div>
  );
});
